import React, { useState, useEffect, useMemo } from 'react';
import { Tag } from 'lucide-react';
import { useFontSizes } from '../../contexts/FontSizeContext';
import { useI18n } from '../../i18n/I18nContext';
import type { WidgetProps } from './types';

interface PromotedBrand {
  id: string;
  name: string;
  description?: string;
  imageUrl?: string;
  order: number;
}

import { getApiBase } from '../../utils/api';

async function fetchPromotedBrands(): Promise<PromotedBrand[]> {
  try {
    const res = await fetch(`${getApiBase()}/promoted-brands`, {
      signal: AbortSignal.timeout(8_000),
    });
    if (!res.ok) return [];
    return (await res.json()) as PromotedBrand[];
  } catch {
    return [];
  }
}

export const BrandsWidget: React.FC<WidgetProps> = ({ config, defaults, onSearch }) => {
  const { heading, body } = useFontSizes();
  const { t } = useI18n();
  const [promotedBrands, setPromotedBrands] = useState<PromotedBrand[] | null>(null);

  useEffect(() => {
    fetchPromotedBrands().then((data) => setPromotedBrands(data));
  }, []);

  // Derive brands from best sellers as a fallback
  const derivedBrands: PromotedBrand[] = useMemo(() => {
    const producers = (defaults?.bestSellers ?? [])
      .map((p) => p.producer)
      .filter((producer): producer is string => Boolean(producer));
    return [...new Set(producers)].map((name, i) => ({ id: name, name, order: i }));
  }, [defaults]);

  const source = promotedBrands !== null && promotedBrands.length > 0
    ? promotedBrands
    : derivedBrands;

  const items = config.maxItems ? source.slice(0, config.maxItems) : source.slice(0, 24);
  if (items.length === 0) return null;

  return (
    <section className="mb-8">
      <h2 className={`flex items-center gap-2 ${heading} font-semibold uppercase tracking-wider text-[#020202] mb-4`}>
        <Tag className="h-4 w-4" />
        {config.title ?? t('section.brands')}
      </h2>
      <div className="flex flex-wrap gap-2">
        {items.map((brand, index) => (
          <button
            key={brand.id}
            type="button"
            onClick={() => onSearch(brand.name)}
            title={brand.description}
            className={`inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-lg ${body} text-gray-700 hover:border-[#722F37]/40 hover:text-[#722F37] hover:shadow-sm transition-all animate-fade-in`}
            style={{ animationDelay: `${index * 20}ms` }}
          >
            {brand.imageUrl && (
              <img
                src={brand.imageUrl}
                alt=""
                className="w-4 h-4 rounded object-cover"
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
              />
            )}
            {brand.name}
          </button>
        ))}
      </div>
    </section>
  );
};
